import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Form, Modal, Row, Spinner } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import Select from 'react-select';
import Swal from 'sweetalert2';

import { blackberry, coolGrey, grape, greyStroke, squidInk, white } from 'lib/css/colors';
import { selectStyles } from 'lib/css/selectStyles';
import { BRANDS_UPDATE_FROM_FSA } from 'lib/networking/endpoints';
import {
  BrandVerificationStatus,
  BrandVerificationStatusOptions,
} from 'lib/enums/BrandVerificationStatus';
import ManufacturerFilter from 'components/manage_brands/brand_details/ManufacturerFilter';
import usePost from 'lib/hooks/usePost';

function BrandEditModal({ brand, show, onHide, refetch }) {
  const [name, setName] = useState(brand.name || '');
  const [verificationStatus, setVerificationStatus] = useState(brand.verification_status);
  const [manufacturer, setManufacturer] = useState();

  const verified = brand.verification_status === BrandVerificationStatus.VERIFIED;
  const isValidName =
    name && name.trim().length > 0 && !['none', 'null'].includes(name.trim().toLowerCase());

  const { postData: updateBrandDetails, loading: updating } = usePost(
    BRANDS_UPDATE_FROM_FSA,
    () => {
      Swal.fire({
        icon: 'success',
        text: 'Brand details updated successfully',
        title: 'Success',
        timer: 500,
      }).then(() => {
        onHide();
        refetch();
      });
    },
    error =>
      Swal.fire({
        icon: 'error',
        text: `An error occurred while updating brand details: ${error?.response?.data?.message}`,
        title: 'Error',
      }),
  );

  return (
    <Modal animation={false} centered show={show} onHide={onHide}>
      <div className={css(styles.modalContent)}>
        <Modal.Header className={css(styles.header)}>
          <Modal.Title className={css(styles.title)}>Edit Brand</Modal.Title>
          <button className={css(styles.closeButton)} onClick={onHide} aria-label="Close">
            &times;
          </button>
        </Modal.Header>
        <Modal.Body className={css(styles.body)}>
          <Row>
            <Col>
              <Form.Group className={css(styles.formGroup)}>
                <Form.Label className={css(styles.label)}>Brand Name</Form.Label>
                <Form.Control
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  disabled={verified}
                  className={css(styles.input)}
                />
              </Form.Group>
              <Form.Group className={css(styles.formGroup)}>
                <Form.Label className={css(styles.label)}>Verification Status</Form.Label>
                <Select
                  isClearable
                  isDisabled={updating}
                  options={BrandVerificationStatusOptions}
                  value={BrandVerificationStatusOptions.find(
                    option => option.value === verificationStatus,
                  )}
                  onChange={option => setVerificationStatus(option?.value)}
                  styles={selectStyles}
                />
              </Form.Group>
              <Form.Group className={css(styles.formGroup)}>
                <Form.Label className={css(styles.label)}>Manufacturer</Form.Label>
                <ManufacturerFilter
                  manufacturerID={brand.manufacturer_id}
                  onChange={setManufacturer}
                  disabled={verified && brand.manufacturer_id}
                  autoFocus={false}
                />
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className={css(styles.footer)}>
          <Button
            className={css(styles.primaryButton)}
            onClick={() =>
              updateBrandDetails({
                id: brand.id,
                name: name.trim(),
                verification_status: verificationStatus,
                manufacturer_id: manufacturer?.value || null,
              })
            }
            disabled={updating || (!verified && !isValidName)}
          >
            {updating ? <Spinner animation="border" role="status" size="sm" /> : 'Update'}
          </Button>
          <Button className={css(styles.cancelButton)} onClick={onHide}>
            Cancel
          </Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
}

const styles = StyleSheet.create({
  modalContent: {
    borderRadius: 12,
    overflow: 'hidden',
  },
  header: {
    backgroundColor: white,
    borderBottom: `1px solid ${greyStroke}`,
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    fontSize: 18,
    fontWeight: 700,
    color: squidInk,
    margin: 0,
  },
  closeButton: {
    background: 'none',
    border: 'none',
    fontSize: 24,
    color: coolGrey,
    cursor: 'pointer',
    padding: 0,
    lineHeight: 1,
    ':hover': {
      color: squidInk,
    },
  },
  body: {
    padding: '24px',
  },
  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: 600,
    color: squidInk,
    marginBottom: 8,
  },
  input: {
    borderRadius: 8,
    border: `1px solid ${greyStroke}`,
    padding: '10px 14px',
    ':focus': {
      borderColor: grape,
      boxShadow: `0 0 0 1px ${grape}`,
    },
  },
  footer: {
    backgroundColor: white,
    borderTop: `1px solid ${greyStroke}`,
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'flex-start',
    gap: 12,
  },
  primaryButton: {
    backgroundColor: blackberry,
    color: white,
    border: 'none',
    borderRadius: 8,
    padding: '10px 24px',
    fontWeight: 600,
    fontSize: 14,
    ':hover': {
      backgroundColor: grape,
    },
    ':disabled': {
      backgroundColor: greyStroke,
      color: coolGrey,
    },
  },
  cancelButton: {
    backgroundColor: 'transparent',
    color: grape,
    border: `1px solid ${grape}`,
    borderRadius: 8,
    padding: '10px 24px',
    fontWeight: 600,
    fontSize: 14,
    ':hover': {
      backgroundColor: `${grape}15`,
    },
  },
});

BrandEditModal.propTypes = {
  brand: PropTypes.object,
  show: PropTypes.bool,
  onHide: PropTypes.func,
  refetch: PropTypes.func,
};

export default BrandEditModal;
